import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, ArrowRight, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { blogPosts, getFeaturedPosts, getRecentPosts } from "@/data/blogPosts";
import NewsletterSubscription from "@/components/NewsletterSubscription";

const regionColors: Record<string, string> = {
  Monaco: "bg-gold/20 text-gold border-gold/30",
  Dubai: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  Italy: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  Spain: "bg-rose-500/20 text-rose-400 border-rose-500/30",
};

const Blog = () => {
  const featuredPosts = getFeaturedPosts();
  const recentPosts = getRecentPosts(12);

  return (
    <>
      <Helmet>
        <title>Luxury Real Estate Insights | Agota Estate Blog</title>
        <meta 
          name="description" 
          content="Expert insights on luxury real estate investment trends in Monaco, Dubai, Italy, and Spain. Market analysis, buying guides, and exclusive opportunities from Agota Estate." 
        />
        <meta name="keywords" content="luxury real estate, Monaco property, Dubai investment, Italian villas, Spanish estates, UHNW, property investment" />
        <link rel="canonical" href="https://agotaestate.com/blog" />
        
        <meta property="og:title" content="Luxury Real Estate Insights | Agota Estate" />
        <meta property="og:description" content="Expert insights on luxury real estate investment in Monaco, Dubai, Italy, and Spain." />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Agota Estate Luxury Real Estate Blog",
            "description": "Expert insights on luxury real estate investment trends",
            "publisher": {
              "@type": "Organization",
              "name": "Agota Estate",
            },
            "blogPost": blogPosts.slice(0, 6).map(post => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt,
              "datePublished": post.publishedAt,
              "author": {
                "@type": "Person",
                "name": post.author,
              },
            })),
          })}
        </script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-charcoal">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <span className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4 block">
                Insights & Analysis
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream mb-6">
                Luxury Real Estate Intelligence
              </h1>
              <p className="font-body text-lg text-cream/70 leading-relaxed">
                Expert market analysis, investment guides, and exclusive insights 
                for discerning buyers across Monaco, Dubai, Italy, and Spain.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Posts */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-2">
                Featured Insights
              </h2>
              <p className="font-body text-muted-foreground">
                Our most essential reading for luxury property investors
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="relative aspect-[4/3] overflow-hidden mb-4">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
                      <span className={`absolute top-4 left-4 px-3 py-1 text-xs uppercase tracking-wider border ${regionColors[post.region]}`}>
                        {post.region}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(post.publishedAt).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {post.readTime} min read
                      </span>
                    </div>
                    <h3 className="font-display text-lg text-foreground group-hover:text-gold transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground line-clamp-2">
                      {post.excerpt}
                    </p>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* All Posts Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-2">
                Latest Articles
              </h2>
              <p className="font-body text-muted-foreground">
                Stay informed with our comprehensive market coverage
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group bg-card border border-border hover:border-gold/30 transition-all duration-300"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex items-center gap-2">
                        <MapPin size={12} className="text-gold" />
                        <span className="font-body text-xs text-cream uppercase tracking-wider">
                          {post.region}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <span className="font-body text-xs uppercase tracking-wider text-gold mb-2 block">
                        {post.category}
                      </span>
                      <h3 className="font-display text-xl text-foreground group-hover:text-gold transition-colors mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="font-body text-sm text-muted-foreground mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{post.author}</span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {post.readTime} min
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
            <NewsletterSubscription source="blog" variant="hero" />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-charcoal">
          <div className="container mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl text-cream mb-4">
                Ready to Invest?
              </h2>
              <p className="font-body text-cream/70 mb-8 max-w-xl mx-auto">
                Our team provides personalized guidance for acquiring exceptional 
                properties across Europe and the Middle East.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="luxury" size="xl" asChild>
                  <Link to="/properties">
                    View Properties
                    <ArrowRight size={18} className="ml-2" />
                  </Link>
                </Button>
                <Button variant="outline" size="xl" asChild className="border-cream/30 text-cream hover:bg-cream/10">
                  <a href="/#contact">Schedule Consultation</a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Blog;
